import { Typography } from '@persona-id/design-tokens';
import jsonLogic, { RulesLogic } from 'json-logic-js';
import { FC, useState } from 'react';
import styled from 'styled-components';

import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { useWorkflowActionMeta } from 'dashboard/components/WorkflowEditor/WorkflowActionMetaProvider/WorkflowActionMetaProvider';
import { CaseTemplateStatus } from 'dashboard/models/case-template';
import { TriggerEvent } from 'dashboard/models/trigger';
import { WorkflowRunSimulateContext } from 'dashboard/models/workflow-run-simulated';
import TriggerOverrideCaseEvent from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/WorkflowSimulateControlPanel/TriggerOverrideEvent/TriggerOverrideCaseEvent';
import TriggerOverrideInquiryEvent from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/WorkflowSimulateControlPanel/TriggerOverrideEvent/TriggerOverrideInquiryEvent';
import TriggerOverrideTxnEvent from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/WorkflowSimulateControlPanel/TriggerOverrideEvent/TriggerOverrideTxnEvent';
import Input from 'design-system/components/Input/Input';
import Spacer from 'design-system/components/Spacer';
import ToggleGroup, { ToggleGroupItem } from 'design-system/components/ToggleGroup';
import { EVENT_TYPE_TO_PLACEHOLDER_TOKEN, EventName, EventType } from 'lib/constants/events';
import { FieldSchema } from 'lib/fields';
import { getOperators, runJsonLogic } from 'lib/utils/jsonLogicRunner';
import { eventTypeFor } from 'lib/workflows';

const StyledToggleGroupItem = styled(ToggleGroupItem)`
  height: 24px;
  ${Typography.TextSmall};
`;

enum TriggerOption {
  New = 'new',
  Existing = 'existing',
}

interface TriggerOverrideEventProps {
  setSimulateContext: (arg: WorkflowRunSimulateContext) => void;
  simulateContext: WorkflowRunSimulateContext;
  setTriggerFieldSchemas: (fieldSchemas: FieldSchema[]) => void;
  triggerFieldSchemas: FieldSchema[];
  trigger: TriggerEvent;
  handleClose: () => void;
  disabled: boolean;
  requiredFields: string[];
  referencedFields: string[];
  triggerFieldDefinitionPanelOpen: boolean;
  toggleTriggerFieldDefinitionPanel: () => void;
  closeTriggerFieldDefinitionPanel: () => void;
}

const buildSelection = (template: { name: string; token: string }) => ({
  label: template.name,
  value: template.token,
});

const TriggerOverrideEvent: FC<TriggerOverrideEventProps> = ({
  simulateContext,
  setSimulateContext,
  setTriggerFieldSchemas,
  triggerFieldSchemas,
  trigger,
  disabled,
  requiredFields,
  referencedFields,
  triggerFieldDefinitionPanelOpen,
  toggleTriggerFieldDefinitionPanel,
  closeTriggerFieldDefinitionPanel,
}) => {
  const [selectedTriggerOption, setSelectedTriggerOption] = useState<TriggerOption>(
    simulateContext?.trigger?.type === 'object' ? TriggerOption.Existing : TriggerOption.New,
  );
  const actionMeta = useWorkflowActionMeta();
  const eventType = eventTypeFor(trigger);
  const triggerObjectId =
    simulateContext?.trigger?.type === 'object'
      ? (simulateContext.trigger['persona-object-id'] ?? '')
      : '';

  const filterTemplatesByTriggerCriteria = (
    templates: { name: string; token: string }[],
    keyForTemplate: string,
  ) => {
    const triggerRules = trigger.attributes.rules;
    const jsonLogicVariables = jsonLogic.uses_data(triggerRules as Record<string, any>);
    const jsonLogicOperators = getOperators(triggerRules as Record<string, any>);
    const hasTriggerCriteria = triggerRules !== true;
    // filtering is only supported for template token criteria
    const hasSupportedJsonLogicVariables =
      jsonLogicVariables.length === 1 &&
      jsonLogicVariables[0] === `trigger.${keyForTemplate}.token`;
    // filtering is only supported for non list related operators
    const hasSupportedJsonLogicOperators =
      !jsonLogicOperators.includes('inlist') && !jsonLogicOperators.includes('ninlist');

    return hasTriggerCriteria && hasSupportedJsonLogicVariables && hasSupportedJsonLogicOperators
      ? templates.filter((template) =>
          runJsonLogic(triggerRules as RulesLogic, {
            trigger: { [keyForTemplate]: { token: template.token } },
          }),
        )
      : templates;
  };

  const handleObjectIdUpdate = (value: string) => {
    setSimulateContext({
      ...simulateContext,
      trigger:
        value === ''
          ? undefined
          : {
              type: 'object',
              'persona-object-id': value,
            },
    });
  };

  const configureNewTriggerObject = (() => {
    switch (eventType) {
      case EventType.Inquiry:
        return (
          <TriggerOverrideInquiryEvent
            simulateContext={simulateContext}
            setSimulateContext={setSimulateContext}
            setTriggerFieldSchemas={setTriggerFieldSchemas}
            triggerFieldSchemas={triggerFieldSchemas}
            templateOptions={filterTemplatesByTriggerCriteria(
              [...actionMeta['inquiry-templates'], ...actionMeta['templates']],
              'template',
            ).map(buildSelection)}
            disabled={disabled}
            requiredFields={requiredFields}
            referencedFields={referencedFields}
            triggerFieldDefinitionPanelOpen={triggerFieldDefinitionPanelOpen}
            toggleTriggerFieldDefinitionPanel={toggleTriggerFieldDefinitionPanel}
            closeTriggerFieldDefinitionPanel={closeTriggerFieldDefinitionPanel}
          />
        );
      case EventType.Case: {
        const filteredCaseTemplates = actionMeta['case-templates'].filter(
          (template) => template.status === CaseTemplateStatus.Active,
        );
        return (
          <TriggerOverrideCaseEvent
            simulateContext={simulateContext}
            setSimulateContext={setSimulateContext}
            setTriggerFieldSchemas={setTriggerFieldSchemas}
            templateOptions={filterTemplatesByTriggerCriteria(
              filteredCaseTemplates,
              'case_template',
            ).map(buildSelection)}
            disabled={disabled}
            requiredFields={requiredFields}
            referencedFields={referencedFields}
            triggerFieldDefinitionPanelOpen={triggerFieldDefinitionPanelOpen}
            toggleTriggerFieldDefinitionPanel={toggleTriggerFieldDefinitionPanel}
            closeTriggerFieldDefinitionPanel={closeTriggerFieldDefinitionPanel}
            allowStatusSelection={trigger.attributes['event-name'] === EventName.CaseStatusUpdated}
          />
        );
      }
      case EventType.Transaction: {
        const allowedTxnTypes = trigger.attributes['template-filters']?.['transaction-type'];
        const options =
          allowedTxnTypes && allowedTxnTypes.length > 0
            ? actionMeta['transaction-types'].filter((txnType) =>
                allowedTxnTypes.includes(txnType.token),
              )
            : actionMeta['transaction-types'];
        return (
          <TriggerOverrideTxnEvent
            simulateContext={simulateContext}
            setSimulateContext={setSimulateContext}
            setTriggerFieldSchemas={setTriggerFieldSchemas}
            templateOptions={options.map(buildSelection)}
            disabled={disabled}
            requiredFields={requiredFields}
            referencedFields={referencedFields}
            triggerFieldDefinitionPanelOpen={triggerFieldDefinitionPanelOpen}
            toggleTriggerFieldDefinitionPanel={toggleTriggerFieldDefinitionPanel}
            closeTriggerFieldDefinitionPanel={closeTriggerFieldDefinitionPanel}
            allowStatusSelection={
              trigger.attributes['event-name'] === EventName.TransactionStatusUpdated
            }
          />
        );
      }
      default:
        return <></>;
    }
  })();

  return (
    <>
      <Spacer height={1} />
      <ToggleGroup
        type='single'
        value={selectedTriggerOption}
        onValueChange={(value: TriggerOption) => setSelectedTriggerOption(value)}
        disabled={disabled}
        fullWidth
      >
        <StyledToggleGroupItem value={TriggerOption.New} aria-label='New trigger'>
          New
        </StyledToggleGroupItem>
        <StyledToggleGroupItem value={TriggerOption.Existing} aria-label='Existing trigger'>
          Existing
        </StyledToggleGroupItem>
      </ToggleGroup>
      {selectedTriggerOption === TriggerOption.New ? (
        configureNewTriggerObject
      ) : (
        <>
          <Spacer height={1} />
          <ParamLabel labelText='Object Id' htmlFor='trigger-override__object-id-input' />
          <Input
            id='trigger-override__object-id-input'
            value={triggerObjectId}
            disabled={disabled}
            onChange={(e) => handleObjectIdUpdate(e.target.value)}
            aria-label='Object id input'
            placeholder={EVENT_TYPE_TO_PLACEHOLDER_TOKEN[eventType]}
          />
        </>
      )}
    </>
  );
};

export default TriggerOverrideEvent;
